// Gary's Comics sorting minigame. Comics come off the pile one at a time, each
// stamped with a genre; drop it in the matching bin before its little timer runs
// out. Faster you go, tighter the clock. Combos build; wrong bin or a timeout
// breaks the streak. This module owns the DOM overlay + scoring; the shop scene
// reacts via onSort (e.g. Gary nods on a good sort).

const DURATION = 40;      // seconds per shift

export const GENRES = [
  { name: 'Action', emoji: '💥', color: '#E6537B' },
  { name: 'Sci-Fi', emoji: '🚀', color: '#3D5FC0' },
  { name: 'Horror', emoji: '👻', color: '#7FBF6A' },
  { name: 'Comedy', emoji: '😂', color: '#F49B33' }
];

export class ComicSort {
  constructor({ onSort, onFinish } = {}){
    this.onSort = onSort || (() => {});
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    this.timeLeft = DURATION;
    this.correct = 0; this.wrong = 0; this.combo = 0; this.bestCombo = 0; this.points = 0;

    this.current = null;      // { genre, budget, left }
    this._onKey = this._onKey.bind(this);
  }

  get budget(){ return Math.max(1.0, 2.3 - this.correct * 0.03); }   // per-comic clock, ramps

  mount(root){
    const el = document.createElement('div');
    el.className = 'sort-hud';
    el.innerHTML = `
      <div class="sort-info">
        <span class="pill sort-timer">0:40</span>
        <span class="pill sort-score">0 sorted</span>
        <span class="pill sort-combo" hidden></span>
      </div>
      <div class="sort-feedback"></div>
      <div class="sort-comic"><span class="sort-emoji"></span><span class="sort-name"></span>
        <div class="sort-comic-timer"><i></i></div>
      </div>
      <div class="sort-bins">
        ${GENRES.map((g, i) => `
          <button class="sort-bin" data-genre="${i}" style="--c:${g.color}">
            <span>${g.emoji}</span><b>${g.name}</b>
          </button>`).join('')}
      </div>`;
    (root || document.body).appendChild(el);
    this.el = el;
    this.timerEl = el.querySelector('.sort-timer');
    this.scoreEl = el.querySelector('.sort-score');
    this.comboEl = el.querySelector('.sort-combo');
    this.feedbackEl = el.querySelector('.sort-feedback');
    this.comicEl = el.querySelector('.sort-comic');
    this.emojiEl = el.querySelector('.sort-emoji');
    this.nameEl = el.querySelector('.sort-name');
    this.barEl = el.querySelector('.sort-comic-timer > i');

    el.querySelectorAll('.sort-bin').forEach(bin => {
      bin.addEventListener('pointerdown', e => { e.preventDefault(); this._sort(Number(bin.dataset.genre)); });
    });
    window.addEventListener('keydown', this._onKey);
  }

  begin(){ this.active = true; this._next(); }

  _onKey(e){
    if (!this.active) return;
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= GENRES.length) this._sort(n - 1);
  }

  _next(){
    const genre = Math.floor(Math.random() * GENRES.length);
    this.current = { genre, budget: this.budget, left: this.budget };
    const g = GENRES[genre];
    this.emojiEl.textContent = g.emoji;
    this.nameEl.textContent = g.name;
    this.comicEl.style.setProperty('--c', g.color);
    this.comicEl.classList.remove('in'); void this.comicEl.offsetWidth; this.comicEl.classList.add('in');
  }

  _sort(binGenre){
    if (!this.active || !this.current) return;
    const right = binGenre === this.current.genre;
    if (right){
      this.correct++; this.combo++; this.bestCombo = Math.max(this.bestCombo, this.combo);
      this.points += 1 + Math.min(this.combo, 10);
      this._feedback('sorted! ✓', 'good');
    } else {
      this.wrong++; this.combo = 0;
      this._feedback('wrong bin! ✗', 'bad');
    }
    this.onSort({ right, combo: this.combo });
    this._renderScore();
    this._next();
  }

  update(dt){
    if (!this.active || this.finished) return;

    this.timeLeft -= dt;
    if (this.timeLeft <= 0){ this.timeLeft = 0; this._renderTimer(); return this._finish(); }
    this._renderTimer();

    if (this.current){
      this.current.left -= dt;
      const f = Math.max(0, this.current.left / this.current.budget);
      this.barEl.style.width = `${f * 100}%`;
      if (this.current.left <= 0){        // timed out — counts as a miss
        this.wrong++; this.combo = 0;
        this._feedback('too slow!', 'bad');
        this.onSort({ right: false, combo: 0 });
        this._renderScore();
        this._next();
      }
    }
  }

  _renderTimer(){
    const s = Math.ceil(this.timeLeft);
    this.timerEl.textContent = `0:${String(s).padStart(2, '0')}`;
    this.timerEl.classList.toggle('low', s <= 5);
  }

  _renderScore(){
    this.scoreEl.textContent = `${this.correct} sorted`;
    if (this.combo >= 2){
      this.comboEl.hidden = false;
      this.comboEl.textContent = `🔥 ${this.combo} combo`;
      this.comboEl.classList.remove('pulse'); void this.comboEl.offsetWidth; this.comboEl.classList.add('pulse');
    } else this.comboEl.hidden = true;
  }

  _feedback(text, cls){
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = text;
    this.feedbackEl.className = `sort-feedback show ${cls}`;
    void this.feedbackEl.offsetWidth; this.feedbackEl.classList.add('anim');
  }

  _finish(){
    if (this.finished) return;
    this.finished = true; this.active = false;
    const total = this.correct + this.wrong;
    const accuracy = total ? Math.round(this.correct / total * 100) : 0;
    const crumbs = this.correct + Math.floor(this.bestCombo / 5);
    this.onFinish({ correct: this.correct, wrong: this.wrong, bestCombo: this.bestCombo, points: this.points, accuracy, crumbs });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    if (this.el) this.el.remove();
    this.el = null;
  }
}
